import { useEffect } from 'react';
/**
 * useEscapeToClose — wire Escape-to-dismiss for hand-rolled modal dialogs.
 *
 * The shared `RrModal` uses the native `<dialog>` element, which gives Esc
 * dismissal for free. The CRM's earlier hand-rolled `role="dialog"` overlays
 * (QuickLogModal, ContactDialog, TransitionStateDialog) render a plain `<div>`
 * with no Esc wiring — so only the Cancel button could dismiss them. This hook
 * restores Esc dismissal for those overlays at the shared layer so every
 * consumer gets the same behavior.
 *
 * Listener is attached to `document` (keydown, capture-free) only while
 * `active` is true, and torn down on unmount / when `active` flips false.
 *
 * RIG-304 — the handler does NOT call `stopPropagation()`: on a capture-free
 * document listener that does nothing for ancestors but would silently
 * suppress other document-level Escape listeners and any focused nested widget
 * (native `<select>`, date picker) that wants Escape to close *itself* first.
 * Instead we let Escape bubble, skip if the event was already consumed
 * (`defaultPrevented`), and mark it consumed via `preventDefault()` so that
 * with multiple stacked overlays only the first instance to see the event
 * closes — not every active instance on a single Escape.
 *
 * @param active  Whether the modal is currently open (listener only attaches
 *                when true).
 * @param onClose Called when Escape is pressed.
 */
export function useEscapeToClose(active, onClose) {
    useEffect(() => {
        if (!active)
            return;
        function handle(e) {
            if (e.key !== 'Escape')
                return;
            // Another handler (an inner widget or a higher overlay) already consumed
            // this Escape — don't double-close.
            if (e.defaultPrevented)
                return;
            // Mark consumed so other stacked overlays sharing this hook skip it.
            e.preventDefault();
            onClose();
        }
        document.addEventListener('keydown', handle);
        return () => {
            document.removeEventListener('keydown', handle);
        };
    }, [active, onClose]);
}
//# sourceMappingURL=useEscapeToClose.js.map